import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCerrarGestion } from '../clientes/gestion/gestionCerrarContext';
import FacturaPOSClientePanel from '../facturaventa/pos/FacturaPOSClientePanel';
import FacturaPOSSearchBar, { SuggestionItem } from '../facturaventa/pos/FacturaPOSSearchBar';
import FacturaPOSLineasGrid from '../facturaventa/pos/FacturaPOSLineasGrid';
import FacturaPOSTotalesPanel, { type POSCompaniaOption } from '../facturaventa/pos/FacturaPOSTotalesPanel';
import { POSCliente, POSLinea } from '../facturaventa/pos/types';
import { pendienteMetaDesdeMaster } from '../facturaventa/pos/pendienteMetaLinea';
import remitoVentaService, { RemitoVentaCreateDto } from '../../services/remitoVentaService';
import { ROUTES } from '../../config/routes';
import ventasSeguimientoService, {
  VentaPendienteMaster,
  cantidadSugeridaDesdePendienteVenta,
} from '../../services/ventasSeguimientoService';
import clienteService, { OPTIONS_CLIENTE_OPERATIVO } from '../../services/clienteService';
import {
  advertirLineasConPrecioCero,
  precioListaDesdePendienteMaster,
} from '../../utils/pedidoVentaLineas';
import {
  cantidadUmIngresoDesdePedidoMaster,
  umDesdePedidoMaster,
} from '../../utils/remitoVentaUmStock';
import { obtenerCodigoListaCliente } from '../../utils/precioListaPedido';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatNroDocumentoVenta, parseLocalDate, toLocalDateStringYMD, truncToDecimals } from '../../utils/formatters';
import { extractLoteFlagsFromProducto, mensajeValidacionLotes } from '../../utils/loteLineaHelpers';
import { cargarProductoParaDocumento } from '../../utils/bloqueaArtDiscontinuados';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { persistGlobalNroSuc } from '../../hooks/useSucursalesVentaFiltro';
import {
  filtrarSucursalesPorCompania,
  resolveNroSucParaCompania,
  resolveSucursalPreferidaNro,
  resolveCompaniaYSucursalInicial,
  persistCompaniaYSucursalInicial,
} from '../../utils/sucursalPreferida';
import { persistGlobalIdCompania } from '../../utils/companiaPreferida';
import companiaService from '../../services/companiaService';
import BuscarPendientesEntregaDialog from '../../components/ventas/BuscarPendientesEntregaDialog';

type RemitoCabecera = {
  nroSuc: number;
  fechaEmision: Date;
  horaEmision: Date;
  nroCliente: number;
  nombre: string;
  cuit: string;
  dni: string;
  tipoIVA: string;
  nroVendedor: number;
  nroLista: string;
  descripcion: string;
  transporte: string;
  ubicacion: string;
  formaDePago: string;
  ordenCompra: string;
  idCompania: string;
  porcDesc: number;
  porcDesc2: number;
  porcDesc3: number;
};

export type RemitoParcialDesdeFactura = {
  nroFormularioFactura: number;
  formulario: {
    nroCliente: number;
    nombre?: string;
    cuit?: string;
    nroLista?: string;
    nroSuc: number;
    descripcion?: string;
    nroUbicacionOrigen?: number;
    porcDesc?: number | null;
    porcDesc2?: number | null;
    porcDesc3?: number | null;
  };
  items: Array<{
    nroFormularioMaster?: number;
    nroItemMaster?: number;
    codigoArticulo: string;
    descripcionArticulo?: string;
    cantidad: number;
    precioUnitario: number;
    porcDesc?: number;
    notas?: string;
    um?: string;
  }>;
};

const FORMAS_DE_PAGO_VALIDAS = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];
const NRO_SUC_REMITO_NUEVO = 0;

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const round2 = (n: number) => Math.round(n * 100) / 100;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number =>
  Math.round(l.cantidad * l.precioUnitario * (1 - l.descuento / 100) * 100) / 100;

const loteFlagsDesdeCodigo = async (
  codigoArticulo: string,
): Promise<{ controlLoteSerie: string; requiereVencimiento: boolean }> => {
  const codigo = String(codigoArticulo ?? '').trim();
  if (!codigo) return { controlLoteSerie: 'N', requiereVencimiento: false };
  try {
    const producto = await cargarProductoParaDocumento(codigo, 'VENTAS');
    return extractLoteFlagsFromProducto(producto);
  } catch {
    return { controlLoteSerie: 'N', requiereVencimiento: false };
  }
};

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) return '';
  const rawValue = cliente.nroExpreso ?? (cliente as { nroexpreso?: number | string | null })?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) return '';
  const strValue = String(rawValue).trim();
  return !strValue || strValue === '0' ? '' : strValue;
};

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) return '';
  const cleaned = String(valor).trim().toUpperCase();
  return FORMAS_DE_PAGO_VALIDAS.find((f) => f.toUpperCase() === cleaned) || '';
};

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) return '';
  return normalizarFormaDePago(cliente.condVta ?? (cliente as { condvta?: string | null })?.condvta ?? null);
};

const normalizarPorcentajeBonificacion = (valor?: number | string | null): number => {
  if (valor === null || valor === undefined) return 0;
  const numero = Number(valor);
  return Number.isFinite(numero) ? Math.max(0, Math.min(100, numero)) : 0;
};

const obtenerNroUbicacionDesdeTexto = (ubicacion?: string): number | undefined => {
  if (!ubicacion) return undefined;
  const match = ubicacion.match(/\((\d+)\)\s*$/);
  if (match?.[1]) return Number(match[1]);
  const soloNumero = Number(ubicacion.trim());
  return Number.isFinite(soloNumero) && soloNumero > 0 ? soloNumero : undefined;
};

const remitoCabeceraInicial: RemitoCabecera = {
  nroSuc: NRO_SUC_REMITO_NUEVO,
  fechaEmision: new Date(),
  horaEmision: new Date(),
  nroCliente: 0,
  nombre: '',
  cuit: '',
  dni: '',
  tipoIVA: '',
  nroVendedor: 0,
  nroLista: '',
  descripcion: '',
  transporte: '',
  ubicacion: '',
  formaDePago: '',
  ordenCompra: '',
  idCompania: '',
  porcDesc: 0,
  porcDesc2: 0,
  porcDesc3: 0,
};

const RemitoVentaForm: React.FC<{
  nroClienteInicial?: number;
  remitoParcialInicial?: RemitoParcialDesdeFactura;
}> = ({ nroClienteInicial, remitoParcialInicial }) => {
  const navigate = useNavigate();
  const cerrarGestion = useCerrarGestion();
  const embebido = cerrarGestion != null;
  const salir = () => {
    if (cerrarGestion) cerrarGestion();
    else navigate('/ventas/remitos');
  };
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const nroClienteQuery = Number(searchParams.get('nroCliente'));
  const nroClientePrecargado = nroClienteInicial && nroClienteInicial > 0
    ? nroClienteInicial
    : Number.isFinite(nroClienteQuery) && nroClienteQuery > 0
      ? nroClienteQuery
      : 0;
  const { enqueueSnackbar } = useSnackbar();
  const {
    sucursales,
    preferidaNro,
    preferidaSucursal,
    loading: loadingSucursalesUsuario,
  } = useSucursalesUsuarioContext();
  const sucursalUsuarioEditadaRef = useRef(false);
  const initCompaniaSucursalRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posCliente, setPosCliente] = useState<POSCliente | null>(null);
  const [lineasPOS, setLineasPOS] = useState<POSLinea[]>([]);
  const [cabecera, setCabecera] = useState<RemitoCabecera>(remitoCabeceraInicial);
  useEffect(() => {
    if (!nroClientePrecargado || nroClientePrecargado <= 0) return;
    setCabecera((prev) =>
      (prev.nroCliente ?? 0) > 0 ? prev : { ...prev, nroCliente: nroClientePrecargado },
    );
  }, [nroClientePrecargado]);
  const [companias, setCompanias] = useState<POSCompaniaOption[]>([]);
  const loadingSucursales = loadingSucursalesUsuario;
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroRemitoCreado, setNroRemitoCreado] = useState<number | null>(null);
  const [comprobanteRemitoCreado, setComprobanteRemitoCreado] = useState<string | null>(null);
  const [pendingApplied, setPendingApplied] = useState(false);
  const [openPendientes, setOpenPendientes] = useState(false);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [pendientes, setPendientes] = useState<VentaPendienteMaster[]>([]);

  const navState = (location.state as {
    ventaPendiente?: VentaPendienteMaster;
    remitoParcialDesdeFactura?: RemitoParcialDesdeFactura;
  } | null);
  const ventaPendiente = navState?.ventaPendiente;
  const remitoParcialDesdeFactura = remitoParcialInicial ?? navState?.remitoParcialDesdeFactura;

  useEffect(() => {
    const cargarCompanias = async () => {
      try {
        const lista = await companiaService.getCompanias({ pageNumber: 1, pageSize: 100 });
        setCompanias(
          lista
            .map((c) => ({
              idCompania: String(c.idCompania ?? '').trim(),
              nombre: String(c.nombre ?? '').trim() || undefined,
              condIva: c.condIva != null ? String(c.condIva).trim() : undefined,
            }))
            .filter((c) => Boolean(c.idCompania)),
        );
      } catch {
        setCompanias([]);
      }
    };
    void cargarCompanias();
  }, []);

  useEffect(() => {
    sucursalUsuarioEditadaRef.current = false;
    initCompaniaSucursalRef.current = false;
    setCabecera((prev) => ({ ...prev, nroSuc: 0, idCompania: '' }));
  }, [location.key]);

  useEffect(() => {
    if (loadingSucursalesUsuario) return;
    if (sucursales.length === 0 || companias.length === 0) return;
    if (sucursalUsuarioEditadaRef.current) return;
    if (remitoParcialDesdeFactura) return;
    if (initCompaniaSucursalRef.current) return;

    const inicial = resolveCompaniaYSucursalInicial({
      sucursales,
      preferidaNro,
      preferidaSucursal,
      companias,
    });
    if (!inicial) return;
    initCompaniaSucursalRef.current = true;
    setCabecera((prev) => ({
      ...prev,
      nroSuc: inicial.nroSuc,
      idCompania: inicial.idCompania,
    }));
    persistCompaniaYSucursalInicial(inicial);
  }, [
    sucursales,
    companias,
    loadingSucursalesUsuario,
    preferidaNro,
    preferidaSucursal,
    location.key,
    remitoParcialDesdeFactura,
  ]);

  const sucursalesDeCompania = useMemo(
    () => filtrarSucursalesPorCompania(sucursales, cabecera.idCompania),
    [sucursales, cabecera.idCompania],
  );

  const handleIdCompaniaChange = useCallback((id: string) => {
    const next = String(id ?? '').trim();
    if (next) persistGlobalIdCompania(next);
    const nro = resolveNroSucParaCompania({
      idCompania: next,
      sucursales,
      preferidaNro,
      storedNroSuc: Number(localStorage.getItem('nroSuc') || '0') || null,
      keepCurrentIfValid: false,
    });
    sucursalUsuarioEditadaRef.current = false;
    setCabecera((prev) => ({
      ...prev,
      idCompania: next,
      nroSuc: nro != null && nro > 0 ? nro : 0,
    }));
    if (nro != null && nro > 0) persistGlobalNroSuc(nro);
  }, [sucursales, preferidaNro]);

  useEffect(() => {
    const preferida = resolveSucursalPreferidaNro(preferidaNro, sucursales);
    if (preferida != null) {
      persistGlobalNroSuc(preferida);
    }
  }, [preferidaNro, sucursales]);

  useEffect(() => {
    if (pendingApplied || !ventaPendiente || remitoParcialDesdeFactura) return;

    let cancelled = false;
    const lineaId = genId();
    setPendingApplied(true);

    void (async () => {
      const cantidadPendienteEntregar = Number(ventaPendiente.pendienteEntregar) || 0;
      const cantidadBasePend =
        cantidadPendienteEntregar > 0
          ? cantidadPendienteEntregar
          : cantidadSugeridaDesdePendienteVenta(ventaPendiente);
      const precioLista = precioListaDesdePendienteMaster(ventaPendiente);
      const porcDesc = Number(ventaPendiente.porcDesc1Master ?? 0) || 0;
      const [ingresoPed, loteFlags] = await Promise.all([
        cantidadUmIngresoDesdePedidoMaster(
          ventaPendiente.nroFormularioMaster,
          ventaPendiente.nroItemMaster,
          ventaPendiente.codigoArticulo,
          cantidadBasePend,
        ),
        loteFlagsDesdeCodigo(ventaPendiente.codigoArticulo || ''),
      ]);
      if (cancelled) return;

      const cantidad = ingresoPed?.cantidad ?? cantidadBasePend;
      const um =
        ingresoPed?.um ||
        (await umDesdePedidoMaster(
          ventaPendiente.nroFormularioMaster,
          ventaPendiente.nroItemMaster,
          ventaPendiente.codigoArticulo,
        ));
      const subtotal = round2(cantidad * (precioLista - (precioLista * porcDesc) / 100));

      setLineasPOS((prev) => {
        const alreadyIncluded = prev.some(
          (l) =>
            l.nroFormularioMaster === ventaPendiente.nroFormularioMaster &&
            l.nroItemMaster === ventaPendiente.nroItemMaster,
        );
        if (alreadyIncluded) return prev;
        return [
          ...prev,
          {
            id: lineaId,
            codigoArticulo: ventaPendiente.codigoArticulo || '',
            descripcionArticulo: ventaPendiente.descripcionArticulo || '',
            cantidad,
            precioUnitario: precioLista,
            descuento: porcDesc,
            subtotal,
            um,
            controlLoteSerie: loteFlags.controlLoteSerie,
            requiereVencimiento: loteFlags.requiereVencimiento,
            nroFormularioMaster: ventaPendiente.nroFormularioMaster,
            nroItemMaster: ventaPendiente.nroItemMaster,
          },
        ];
      });

      if (ventaPendiente.nroCliente && ventaPendiente.nroCliente > 0) {
        setCabecera((prev) => ({
          ...prev,
          nroCliente: ventaPendiente.nroCliente!,
          nombre: ventaPendiente.nombreCliente || prev.nombre,
        }));
        setPosCliente((prev) =>
          prev && prev.nroCliente === ventaPendiente.nroCliente
            ? prev
            : {
                nroCliente: ventaPendiente.nroCliente!,
                nombre: ventaPendiente.nombreCliente || '',
              },
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingApplied, remitoParcialDesdeFactura, ventaPendiente]);

  useEffect(() => {
    if (pendingApplied || !remitoParcialDesdeFactura) return;

    const form = remitoParcialDesdeFactura.formulario;
    const lineas = remitoParcialDesdeFactura.items.map((item) => {
      const porcDesc = Number(item.porcDesc ?? 0) || 0;
      const precioLista = Number(item.precioUnitario ?? 0);
      const cantidad = Number(item.cantidad ?? 0) || 0;
      return {
        id: genId(),
        codigoArticulo: item.codigoArticulo || '',
        descripcionArticulo: item.descripcionArticulo || '',
        cantidad,
        precioUnitario: precioLista,
        descuento: porcDesc,
        subtotal: round2(cantidad * (precioLista - (precioLista * porcDesc) / 100)),
        um: item.um || '',
        notas: item.notas || '',
        nroFormularioMaster: item.nroFormularioMaster,
        nroItemMaster: item.nroItemMaster,
      };
    });

    setLineasPOS(lineas);
    sucursalUsuarioEditadaRef.current = true;
    setCabecera((prev) => ({
      ...prev,
      nroCliente: Number(form.nroCliente ?? prev.nroCliente ?? 0),
      nombre: form.nombre ?? prev.nombre,
      cuit: form.cuit ?? prev.cuit,
      nroLista: form.nroLista ?? prev.nroLista,
      nroSuc: Number(form.nroSuc ?? prev.nroSuc ?? 0),
      descripcion: form.descripcion ?? prev.descripcion,
      porcDesc: Number(form.porcDesc ?? prev.porcDesc ?? 0),
      porcDesc2: Number(form.porcDesc2 ?? prev.porcDesc2 ?? 0),
      porcDesc3: Number(form.porcDesc3 ?? prev.porcDesc3 ?? 0),
      ubicacion:
        form.nroUbicacionOrigen && form.nroUbicacionOrigen > 0
          ? String(form.nroUbicacionOrigen)
          : prev.ubicacion,
    }));
    setPosCliente({
      nroCliente: Number(form.nroCliente ?? 0),
      nombre: form.nombre || '',
      cuit: form.cuit || '',
      nroLista: form.nroLista || '',
    });
    setPendingApplied(true);
  }, [pendingApplied, remitoParcialDesdeFactura]);

  useEffect(() => {
    const value = cabecera.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString(), 10);
      if (isNaN(nro) || nro <= 0) return;
      void (async () => {
        try {
          const c = await clienteService.getCliente(nro, OPTIONS_CLIENTE_OPERATIVO);
          const transporteSugerido = obtenerTransporteSugerido(c);
          const formaPagoSugerida = obtenerFormaPagoSugerida(c);
          const nroLista = obtenerCodigoListaCliente(c);
          setPosCliente({
            nroCliente: c.nroCliente,
            nombre: c.nombre,
            cuit: c.cuit || '',
            dni: (c as { dni?: string }).dni || '',
            tipoIva: c.tipoIva || '',
            nroLista,
            condVta: c.condVta || '',
            nroVendedor: Number((c as { vendedor?: number }).vendedor) > 0
              ? Number((c as { vendedor?: number }).vendedor)
              : undefined,
            nroExpreso: c.nroExpreso,
            porcDesc: normalizarPorcentajeBonificacion(
              c.porcDesc ?? (c as { porc_Desc?: number }).porc_Desc,
            ),
            porcDesc2: normalizarPorcentajeBonificacion(
              c.porcDesc2 ?? (c as { porc_Desc2?: number }).porc_Desc2,
            ),
            porcDesc3: normalizarPorcentajeBonificacion(
              c.porcDesc3 ?? (c as { porc_Desc3?: number }).porc_Desc3,
            ),
          });
          setCabecera((prev) => ({
            ...prev,
            nroCliente: c.nroCliente,
            nombre: prev.nombre || c.nombre,
            cuit: prev.cuit || c.cuit || '',
            dni: prev.dni || (c as { dni?: string }).dni || '',
            tipoIVA: prev.tipoIVA || c.tipoIva || '',
            nroVendedor:
              prev.nroVendedor > 0
                ? prev.nroVendedor
                : Number((c as { vendedor?: number }).vendedor) > 0
                  ? Number((c as { vendedor?: number }).vendedor)
                  : 0,
            nroLista: prev.nroLista || nroLista,
            formaDePago: prev.formaDePago || formaPagoSugerida,
            transporte: prev.transporte || transporteSugerido,
            porcDesc:
              prev.porcDesc ||
              normalizarPorcentajeBonificacion(c.porcDesc ?? (c as { porc_Desc?: number }).porc_Desc),
            porcDesc2:
              prev.porcDesc2 ||
              normalizarPorcentajeBonificacion(
                c.porcDesc2 ?? (c as { porc_Desc2?: number }).porc_Desc2,
              ),
            porcDesc3:
              prev.porcDesc3 ||
              normalizarPorcentajeBonificacion(
                c.porcDesc3 ?? (c as { porc_Desc3?: number }).porc_Desc3,
              ),
          }));
        } catch {
          /* ignore */
        }
      })();
    }, 500);
    return () => clearTimeout(handle);
  }, [cabecera.nroCliente]);

  const handleClienteSelect = (cliente: POSCliente) => {
    const c = cliente as POSCliente & {
      codigo?: number;
      vendedor?: number;
      porc_Desc?: number;
      porc_Desc2?: number;
      porc_Desc3?: number;
    };
    const nroCliente = Number(c.nroCliente ?? c.codigo ?? 0);
    const nroLista = obtenerCodigoListaCliente(c);
    const posCli: POSCliente = {
      ...c,
      nroCliente,
      nombre: c.nombre || '',
      nroLista: nroLista || c.nroLista,
    };
    setPosCliente(posCli);
    setCabecera((prev) => ({
      ...prev,
      nroCliente,
      nombre: c.nombre || '',
      cuit: c.cuit || '',
      dni: c.dni || '',
      tipoIVA: c.tipoIva || '',
      nroVendedor: Number(c.vendedor) > 0 ? Number(c.vendedor) : prev.nroVendedor,
      formaDePago: obtenerFormaPagoSugerida(c),
      transporte: obtenerTransporteSugerido(c) || prev.transporte,
      porcDesc: normalizarPorcentajeBonificacion(c.porcDesc ?? c.porc_Desc),
      porcDesc2: normalizarPorcentajeBonificacion(c.porcDesc2 ?? c.porc_Desc2),
      porcDesc3: normalizarPorcentajeBonificacion(c.porcDesc3 ?? c.porc_Desc3),
      nroLista: nroLista || prev.nroLista,
    }));
  };

  const handleAgregarProductoPOS = (item: SuggestionItem) => {
    const cantidad = Number(item.cantidad) > 0 ? Number(item.cantidad) : 1;
    const loteFlags = extractLoteFlagsFromProducto(item.productoIva ?? item);
    setLineasPOS((prev) => [
      ...prev,
      {
        id: genId(),
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        cantidad,
        precioUnitario: item.precioUnitario,
        descuento: 0,
        subtotal: calcularSubtotal({
          cantidad,
          precioUnitario: item.precioUnitario,
          descuento: 0,
        }),
        um: item.um,
        stockDisponible: item.stockDisponible,
        controlLoteSerie: loteFlags.controlLoteSerie,
        requiereVencimiento: loteFlags.requiereVencimiento,
      },
    ]);
  };

  const handleLineaChange = (id: string, field: keyof POSLinea, value: string | number) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleLineaFilled = (id: string, data: Partial<POSLinea>) => {
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleEliminarLinea = (id: string) => {
    setLineasPOS((prev) => prev.filter((l) => l.id !== id));
  };

  const handleDuplicarLinea = (id: string) => {
    setLineasPOS((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  };

  const handleAgregarLineaVacia = () => {
    setLineasPOS((prev) => [
      ...prev,
      {
        id: genId(),
        codigoArticulo: '',
        descripcionArticulo: '',
        cantidad: 1,
        precioUnitario: 0,
        descuento: 0,
        subtotal: 0,
        um: '',
      },
    ]);
  };

  const pendienteYaEnLineas = useCallback(
    (p: VentaPendienteMaster) =>
      lineasPOS.some(
        (l) =>
          l.nroFormularioMaster === p.nroFormularioMaster &&
          l.nroItemMaster === p.nroItemMaster,
      ),
    [lineasPOS],
  );

  const openBuscarPendientes = async () => {
    const nroCliente = cabecera.nroCliente || posCliente?.nroCliente || 0;
    if (!nroCliente || nroCliente <= 0) {
      enqueueSnackbar('Seleccioná un cliente para buscar pendientes', { variant: 'warning' });
      return;
    }
    setOpenPendientes(true);
    setLoadingPendientes(true);
    setPendientes([]);
    try {
      const data = await ventasSeguimientoService.getPendientesMaster(nroCliente, true);
      const rows = (data || [])
        .filter((p) => Number(p.pendienteEntregar) > 0)
        .filter((p) => !pendienteYaEnLineas(p));
      setPendientes(rows);
    } catch {
      setPendientes([]);
      enqueueSnackbar('No se pudieron cargar los pendientes de entrega', { variant: 'error' });
    } finally {
      setLoadingPendientes(false);
    }
  };

  const lineaDesdePendiente = (
    p: VentaPendienteMaster,
    cantidadOverride?: number,
  ): POSLinea | null => {
    const codigo = String(p.codigoArticulo || '').trim();
    if (!codigo) return null;
    const cantidadPendienteEntregar = Number(p.pendienteEntregar) || 0;
    const cantidadDefault =
      cantidadPendienteEntregar > 0
        ? cantidadPendienteEntregar
        : cantidadSugeridaDesdePendienteVenta(p);
    const cantidad = cantidadOverride ?? cantidadDefault;
    if (!(cantidad > 0)) return null;
    const precioLista = precioListaDesdePendienteMaster(p);
    const porcDesc = Number(p.porcDesc1Master ?? 0) || 0;
    return {
      id: genId(),
      codigoArticulo: codigo,
      descripcionArticulo: p.descripcionArticulo || '',
      cantidad,
      precioUnitario: precioLista,
      descuento: porcDesc,
      subtotal: round2(cantidad * (precioLista - (precioLista * porcDesc) / 100)),
      um: '',
      notas: p.notas || undefined,
      nroFormularioMaster: p.nroFormularioMaster,
      nroItemMaster: p.nroItemMaster,
      nroClienteMaster: Number(p.nroCliente) > 0 ? Number(p.nroCliente) : undefined,
      pendienteMeta: pendienteMetaDesdeMaster(p),
    };
  };

  const agregarPendiente = async (p: VentaPendienteMaster, cantidad: number) => {
    if (pendienteYaEnLineas(p)) {
      enqueueSnackbar('Ese pendiente ya fue agregado', { variant: 'info' });
      return;
    }
    if (!(cantidad > 0)) {
      enqueueSnackbar('La cantidad debe ser mayor a cero', { variant: 'warning' });
      return;
    }
    const linea = lineaDesdePendiente(p, cantidad);
    if (!linea) {
      enqueueSnackbar('El artículo no tiene código', { variant: 'warning' });
      return;
    }
    const [ingresoPed, loteFlags] = await Promise.all([
      cantidadUmIngresoDesdePedidoMaster(
        p.nroFormularioMaster,
        p.nroItemMaster,
        p.codigoArticulo,
        cantidad,
      ),
      loteFlagsDesdeCodigo(linea.codigoArticulo),
    ]);
    const um =
      ingresoPed?.um ||
      linea.um ||
      (await umDesdePedidoMaster(p.nroFormularioMaster, p.nroItemMaster, p.codigoArticulo));
    const cantidadIngreso = ingresoPed?.cantidad ?? cantidad;
    const precioUnitario = Number(linea.precioUnitario ?? 0);
    const porcDesc = Number(linea.descuento ?? 0);
    setLineasPOS((prev) => [
      ...prev,
      {
        ...linea,
        cantidad: cantidadIngreso,
        um,
        subtotal: round2(
          cantidadIngreso * (precioUnitario - (precioUnitario * porcDesc) / 100),
        ),
        controlLoteSerie: loteFlags.controlLoteSerie,
        requiereVencimiento: loteFlags.requiereVencimiento,
      },
    ]);
    setPendientes((prev) =>
      prev.filter(
        (row) =>
          !(
            row.nroFormularioMaster === p.nroFormularioMaster &&
            row.nroItemMaster === p.nroItemMaster
          ),
      ),
    );
    enqueueSnackbar('Línea agregada desde pendientes', { variant: 'success' });
  };

  const agregarTodosPendientes = async (
    items: Array<{ pendiente: VentaPendienteMaster; cantidad: number }>,
  ) => {
    let agregadas = 0;
    let omitidas = 0;
    const nuevas: POSLinea[] = [];
    const keysAgregadas = new Set<string>();
    const loteFlagsCache = new Map<string, { controlLoteSerie: string; requiereVencimiento: boolean }>();

    for (const { pendiente, cantidad } of items) {
      if (!(cantidad > 0)) {
        omitidas += 1;
        continue;
      }
      if (pendienteYaEnLineas(pendiente) || keysAgregadas.has(`${pendiente.nroFormularioMaster}-${pendiente.nroItemMaster}`)) {
        omitidas += 1;
        continue;
      }
      const linea = lineaDesdePendiente(pendiente, cantidad);
      if (!linea) {
        omitidas += 1;
        continue;
      }
      const codigo = String(linea.codigoArticulo ?? '').trim();
      let loteFlags = loteFlagsCache.get(codigo);
      if (!loteFlags) {
        loteFlags = await loteFlagsDesdeCodigo(codigo);
        if (codigo) loteFlagsCache.set(codigo, loteFlags);
      }
      const ingresoPed = await cantidadUmIngresoDesdePedidoMaster(
        pendiente.nroFormularioMaster,
        pendiente.nroItemMaster,
        pendiente.codigoArticulo,
        cantidad,
      );
      const um =
        ingresoPed?.um ||
        linea.um ||
        (await umDesdePedidoMaster(
          pendiente.nroFormularioMaster,
          pendiente.nroItemMaster,
          pendiente.codigoArticulo,
        ));
      const cantidadIngreso = ingresoPed?.cantidad ?? cantidad;
      const precioUnitario = Number(linea.precioUnitario ?? 0);
      const porcDesc = Number(linea.descuento ?? 0);
      nuevas.push({
        ...linea,
        cantidad: cantidadIngreso,
        um,
        subtotal: round2(
          cantidadIngreso * (precioUnitario - (precioUnitario * porcDesc) / 100),
        ),
        controlLoteSerie: loteFlags.controlLoteSerie,
        requiereVencimiento: loteFlags.requiereVencimiento,
      });
      keysAgregadas.add(`${pendiente.nroFormularioMaster}-${pendiente.nroItemMaster}`);
      agregadas += 1;
    }

    if (nuevas.length > 0) {
      setLineasPOS((prev) => [...prev, ...nuevas]);
      setPendientes((prev) =>
        prev.filter(
          (row) => !keysAgregadas.has(`${row.nroFormularioMaster}-${row.nroItemMaster}`),
        ),
      );
    }

    if (agregadas > 0) {
      enqueueSnackbar(
        omitidas > 0
          ? `Se agregaron ${agregadas} líneas (${omitidas} omitidas).`
          : `Se agregaron ${agregadas} líneas desde pendientes.`,
        { variant: 'success' },
      );
      setOpenPendientes(false);
    } else {
      enqueueSnackbar('No se pudo agregar ninguna línea desde pendientes', { variant: 'info' });
    }
  };

  const subtotalNetoPOS = lineasPOS.reduce((acc, l) => acc + l.subtotal, 0);
  const importeDesc1POS = (subtotalNetoPOS * (cabecera.porcDesc || 0)) / 100;
  const baseDesc2POS = subtotalNetoPOS - importeDesc1POS;
  const importeDesc2POS = (baseDesc2POS * (cabecera.porcDesc2 || 0)) / 100;
  const baseDesc3POS = baseDesc2POS - importeDesc2POS;
  const importeDesc3POS = (baseDesc3POS * (cabecera.porcDesc3 || 0)) / 100;
  const subtotalConDescPOS = baseDesc3POS - importeDesc3POS;
  const ivaPOS = truncToDecimals(subtotalConDescPOS * 0.21, 2);
  const totalPOS = truncToDecimals(subtotalConDescPOS + ivaPOS, 2);

  const handlePorcDescChangePOS = (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', v: number) => {
    setCabecera((prev) => ({ ...prev, [field]: Number.isFinite(v) ? v : 0 }));
  };

  const handleSave = async () => {
    setError(null);

    if (!cabecera.nroCliente || cabecera.nroCliente <= 0) {
      enqueueSnackbar('Debe seleccionar un cliente', { variant: 'warning' });
      return;
    }
    if (!lineasPOS.length) {
      enqueueSnackbar('Debe agregar al menos un artículo', { variant: 'warning' });
      return;
    }
    if (!cabecera.nroSuc || cabecera.nroSuc <= 0) {
      enqueueSnackbar('Debe seleccionar una sucursal válida', { variant: 'warning' });
      return;
    }

    const itemsInvalidos = lineasPOS.filter((d) => {
      if (!d.codigoArticulo || Number(d.cantidad ?? 0) <= 0) return true;
      const precio = Number(d.precioUnitario);
      return d.precioUnitario == null || !Number.isFinite(precio) || precio < 0;
    });
    if (itemsInvalidos.length > 0) {
      enqueueSnackbar('Hay artículos con código o cantidad inválidos', { variant: 'error' });
      return;
    }

    const loteErr = mensajeValidacionLotes(lineasPOS);
    if (loteErr) {
      enqueueSnackbar(loteErr, { variant: 'error' });
      return;
    }

    advertirLineasConPrecioCero(
      lineasPOS.map((d) => ({
        precioUnitario: d.precioUnitario,
        descuento: d.descuento,
        porcDesc: d.descuento,
        codigoArticulo: d.codigoArticulo,
        descripcionArticulo: d.descripcionArticulo,
      })),
      enqueueSnackbar,
      'remito',
    );

    const payload: RemitoVentaCreateDto = {
      formulario: {
        ...(String(cabecera.idCompania ?? '').trim()
          ? { idCompania: String(cabecera.idCompania).trim() }
          : {}),
        descripcion: cabecera.descripcion || undefined,
        nroSuc: cabecera.nroSuc || undefined,
        fechaEmision: cabecera.fechaEmision ? new Date(cabecera.fechaEmision) : new Date(),
        horaEmision: cabecera.horaEmision ? new Date(cabecera.horaEmision) : new Date(),
        fechaVencim: cabecera.fechaEmision ? new Date(cabecera.fechaEmision) : new Date(),
        nroCliente: cabecera.nroCliente,
        nombre: cabecera.nombre || undefined,
        cuit: cabecera.cuit || undefined,
        nroLista: cabecera.nroLista || obtenerCodigoListaCliente(posCliente) || undefined,
        nroUbicacionOrigen: obtenerNroUbicacionDesdeTexto(cabecera.ubicacion),
        formaEntrega: cabecera.transporte || undefined,
        porcDesc: cabecera.porcDesc || 0,
        porcDesc2: cabecera.porcDesc2 || 0,
        porcDesc3: cabecera.porcDesc3 || 0,
        nroFormularioFactura: remitoParcialDesdeFactura?.nroFormularioFactura,
      },
      items: lineasPOS.map((d) => ({
        codigoArticulo: String(d.codigoArticulo ?? '').trim(),
        descripcion: d.descripcionArticulo || undefined,
        cantidad: Number(d.cantidad ?? 0),
        precioUnitario: Number(d.precioUnitario ?? 0),
        porcDesc: Number(d.descuento ?? 0),
        nroFormularioMaster: d.nroFormularioMaster,
        nroItemMaster: d.nroItemMaster,
        notas: d.notas || undefined,
        um: d.um || undefined,
        lotes: d.lotes,
      })),
    };

    try {
      setLoading(true);
      const creado = await remitoVentaService.createRemitoVenta(payload);
      const nroDoc = formatNroDocumentoVenta(creado.letra, creado.nroSuc, creado.nroComprobante);
      setNroRemitoCreado(creado.nroFormulario);
      setComprobanteRemitoCreado(nroDoc || null);
      setSuccessDialogOpen(true);
      enqueueSnackbar(
        nroDoc ? `Remito ${nroDoc} creado correctamente` : 'Remito creado correctamente',
        { variant: 'success' },
      );
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Error al crear el remito');
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccessDialogOpen(false);
    if (cerrarGestion) {
      cerrarGestion();
      return;
    }
    if (nroRemitoCreado && nroRemitoCreado > 0) {
      navigate(ROUTES.ventas.remitos.ver(nroRemitoCreado));
      return;
    }
    navigate(ROUTES.ventas.remitos.list);
  };

  const nroLista = obtenerCodigoListaCliente(posCliente) || String(cabecera.nroLista ?? '').trim() || undefined;

  return (
    <Box sx={embebido
      ? { maxWidth: '100%', overflowX: 'hidden' }
      : { p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={embebido
        ? { bgcolor: 'transparent', boxShadow: 'none', borderRadius: 0, overflow: 'hidden' }
        : { bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={embebido ? { p: 0, '&:last-child': { pb: 0 } } : { p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => salir()}
              sx={{ textTransform: 'none' }}
            >
              {embebido ? 'Volver' : 'Remitos de Venta'}
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Nuevo Remito POS
            </Typography>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                    Cliente
                  </Typography>
                  <FacturaPOSClientePanel
                    cliente={posCliente}
                    onClienteSelect={handleClienteSelect}
                    formaDePago={cabecera.formaDePago || ''}
                    ordenCompra={cabecera.ordenCompra || ''}
                  />
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                    Artículos
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      alignItems: { xs: 'stretch', sm: 'center' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <FacturaPOSSearchBar
                        nroLista={nroLista}
                        disabled={!posCliente}
                        onAgregarProducto={handleAgregarProductoPOS}
                        contextoUm="remito"
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      onClick={() => void openBuscarPendientes()}
                      disabled={!posCliente || !(posCliente.nroCliente > 0)}
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Buscar pendientes
                    </Button>
                  </Box>
                </Box>
                <Box>
                  {lineasPOS.length > 0 && (
                    <Stack direction="row" justifyContent="flex-end" mb={0.75}>
                      <Chip
                        label={`${lineasPOS.length} renglon${lineasPOS.length > 1 ? 'es' : ''}`}
                        size="small"
                      />
                    </Stack>
                  )}
                  <FacturaPOSLineasGrid
                    lineas={lineasPOS}
                    nroLista={nroLista}
                    contextoUm="remito"
                    loteAsignacion={
                      (() => {
                        const nUb = obtenerNroUbicacionDesdeTexto(cabecera.ubicacion) ?? 0;
                        return nUb > 0 ? { mode: 'egreso' as const, nroUbicacion: nUb } : null;
                      })()
                    }
                    onLineaChange={handleLineaChange}
                    onLineaFilled={handleLineaFilled}
                    onEliminarLinea={handleEliminarLinea}
                    onDuplicarLinea={handleDuplicarLinea}
                    onAgregarLineaVacia={handleAgregarLineaVacia}
                    emptyHint="Buscá un artículo arriba para agregarlo al remito"
                  />
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                <FacturaPOSTotalesPanel
                  subtotalNeto={subtotalNetoPOS}
                  porcDesc={cabecera.porcDesc || 0}
                  porcDesc2={cabecera.porcDesc2 || 0}
                  porcDesc3={cabecera.porcDesc3 || 0}
                  onPorcDescChange={handlePorcDescChangePOS}
                  impuestos={ivaPOS > 0 ? [{ tasa: 21, monto: ivaPOS }] : []}
                  montoIVA={ivaPOS}
                  total={totalPOS}
                  observaciones={cabecera.descripcion ?? ''}
                  onObservacionesChange={(v) => setCabecera((prev) => ({ ...prev, descripcion: v }))}
                  cliente={posCliente}
                  fechaEmision={toLocalDateStringYMD(
                    cabecera.fechaEmision instanceof Date
                      ? cabecera.fechaEmision
                      : new Date(cabecera.fechaEmision),
                  )}
                  onFechaEmisionChange={(ymd) =>
                    setCabecera((prev) => ({ ...prev, fechaEmision: parseLocalDate(ymd) }))
                  }
                  sucursales={sucursalesDeCompania}
                  nroSuc={Number(cabecera.nroSuc) || 0}
                  onNroSucChange={(n) => {
                    sucursalUsuarioEditadaRef.current = true;
                    const suc = sucursales.find((s) => s.nroSuc === n);
                    const idFromSuc = String(suc?.idCompania ?? '').trim();
                    setCabecera((prev) => ({
                      ...prev,
                      nroSuc: n,
                      ...(idFromSuc ? { idCompania: idFromSuc } : {}),
                    }));
                    if (idFromSuc) persistGlobalIdCompania(idFromSuc);
                  }}
                  loadingSucursales={loadingSucursales}
                  idCompania={cabecera.idCompania ?? ''}
                  onIdCompaniaChange={handleIdCompaniaChange}
                  companias={companias}
                  nroVendedor={Number(cabecera.nroVendedor) || 0}
                  nombreVendedorFallback={posCliente?.vendedorInfo?.nombreVendedor ?? undefined}
                  onNroVendedorChange={(nro, nombre) => {
                    setCabecera((prev) => ({ ...prev, nroVendedor: nro ?? 0 }));
                    setPosCliente((prev) => {
                      if (!prev) return prev;
                      if (!nro) return { ...prev, nroVendedor: undefined, vendedorInfo: undefined };
                      return {
                        ...prev,
                        nroVendedor: nro,
                        vendedorInfo: { nroVendedor: nro, nombreVendedor: nombre },
                      };
                    });
                  }}
                  codigoLista={nroLista || ''}
                  onCodigoListaChange={(codigo, descripcion) => {
                    const c = String(codigo ?? '').trim();
                    setPosCliente((prev) => {
                      if (!prev) return prev;
                      if (!c) return { ...prev, nroLista: undefined, listaPrecio: undefined };
                      return {
                        ...prev,
                        nroLista: c,
                        listaPrecio: {
                          codigoLista: c,
                          descripcion: descripcion ?? prev.listaPrecio?.descripcion ?? null,
                        },
                      };
                    });
                    setCabecera((prev) => ({ ...prev, nroLista: c }));
                  }}
                  formaDePago={cabecera.formaDePago || ''}
                  onFormaDePagoChange={(v) => setCabecera((prev) => ({ ...prev, formaDePago: v }))}
                  ordenCompra={cabecera.ordenCompra || ''}
                  onOrdenCompraChange={(v) => setCabecera((prev) => ({ ...prev, ordenCompra: v }))}
                  transporte={cabecera.transporte || ''}
                  onTransporteChange={(v) => setCabecera((prev) => ({ ...prev, transporte: v }))}
                  ubicacion={cabecera.ubicacion || ''}
                  onUbicacionChange={(v) => setCabecera((prev) => ({ ...prev, ubicacion: v }))}
                  saving={loading}
                  onGuardar={() => handleSave()}
                  cantLineas={lineasPOS.length}
                  hideDraftButton
                  saveLabel="Guardar Remito"
                  emisionNombre={posCliente?.nombre ?? cabecera.nombre ?? ''}
                  onEmisionNombreChange={(v) => {
                    setPosCliente((p) => (p ? { ...p, nombre: v, razonSocial: v } : p));
                    setCabecera((prev) => ({ ...prev, nombre: v }));
                  }}
                  emisionCuit={posCliente?.cuit ?? cabecera.cuit ?? ''}
                  onEmisionCuitChange={(v) => {
                    setPosCliente((p) => (p ? { ...p, cuit: v } : p));
                    setCabecera((prev) => ({ ...prev, cuit: v }));
                  }}
                  emisionTipoIva={posCliente?.tipoIva ?? cabecera.tipoIVA ?? ''}
                  onEmisionTipoIvaChange={(v) => {
                    setPosCliente((p) => (p ? { ...p, tipoIva: v } : p));
                    setCabecera((prev) => ({ ...prev, tipoIVA: v }));
                  }}
                  nroInterno={nroRemitoCreado || 0}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <BuscarPendientesEntregaDialog
        open={openPendientes}
        loading={loadingPendientes}
        pendientes={pendientes}
        onClose={() => setOpenPendientes(false)}
        onAgregar={agregarPendiente}
        onAgregarTodos={agregarTodosPendientes}
      />

      <Dialog open={successDialogOpen} onClose={handleCloseSuccess} maxWidth="xs" fullWidth>
        <DialogTitle>Remito creado</DialogTitle>
        <DialogContent>
          <Typography>
            Remito creado exitosamente.{comprobanteRemitoCreado ? ` Comprobante: ${comprobanteRemitoCreado}` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccess}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemitoVentaForm;
